import { achievementApi, type StudentConceptChipAchievement } from '@mathflat/domain/Achievement'
import { type RecentChallengeCurriculum, studentHomeApi } from '@mathflat/domain/StudentHome'
import { makeAutoObservable } from 'mobx'

import { getGradeSubjectSemesterRevisionToKor } from '~/@common/utils/grade'

/**
 * 선별유형칩 커리큘럼 (curriculum에 따른)
 * littleChapter // 선별유형칩
 * currentLittelChapterConceptChipList // 현재 커리큘럼에 해당하는 유형칩 리스트
 * currentLittleChapterId
 * currentLittleChapterTitle
 *
 */

class RecentChallengeByCurriculumService {
  private _recentChallengeByCurriculum: RecentChallengeCurriculum | null = null
  private _currentIndex = 0
  private _achievements: StudentConceptChipAchievement[] = []

  constructor() {
    makeAutoObservable(this)
  }

  get littleChapterConceptChips() {
    return this._recentChallengeByCurriculum?.littleChapterConceptChips
  }

  get currentLittleChapterConceptChips() {
    if (!this.littleChapterConceptChips || this.littleChapterConceptChips.length === 0) {
      return undefined
    }
    return this.littleChapterConceptChips[this._currentIndex]
  }

  get canNavigatePrevious() {
    return this._currentIndex > 0
  }

  get canNavigateNext() {
    const totalCount = this.littleChapterConceptChips?.length ?? 0
    return this._currentIndex < totalCount - 1
  }

  async navigateToPrevious(studentId: string) {
    if (this.canNavigatePrevious) {
      this._currentIndex -= 1
      await this.fetchAchievements(studentId)
    }
  }

  async navigateToNext(studentId: string) {
    if (this.canNavigateNext) {
      this._currentIndex += 1
      await this.fetchAchievements(studentId)
    }
  }

  async fetchAchievements(studentId: StudentConceptChipAchievement['studentId']) {
    const curriculumKey = this.currentLittleChapterConceptChips?.littleChapterCurriculumKey
    if (!curriculumKey) return

    // 실패하면 비운다. 이전 소단원의 성취도가 남으면 다른 칩 목록 위에 색이 얹힌다.
    this._achievements = await achievementApi
      .getStudentConceptChipAchievements(studentId, {
        curriculumKey,
      })
      .catch(() => [])
  }

  get achievements() {
    return this._achievements
  }

  get isAllSmile() {
    return (
      this.currentLittleChapterConceptChips?.conceptChips.length === this.achievements.length &&
      this.achievements.every((item) => item.achievementLevel === 'SMILE')
    )
  }

  get 개념_유형칩리스트() {
    return this.currentLittleChapterConceptChips?.conceptChips.filter(
      (item) => item.conceptChipType === '개념',
    )
  }

  get 기본_유형칩리스트() {
    return this.currentLittleChapterConceptChips?.conceptChips.filter(
      (item) => item.conceptChipType === '기본',
    )
  }

  get 심화_유형칩리스트() {
    return this.currentLittleChapterConceptChips?.conceptChips.filter(
      (item) => item.conceptChipType === '심화',
    )
  }

  get curriculum() {
    return this._recentChallengeByCurriculum?.curriculum
  }

  get curriculumToStr() {
    return (
      this.curriculum &&
      getGradeSubjectSemesterRevisionToKor({
        ...this.curriculum?.school,
        revision: this.curriculum?.revision,
      })
    )
  }

  async fetchRecentChallengeByCurriculum(studentId: string) {
    const data = await studentHomeApi.getRecentChallengeCurriculum()
    this._recentChallengeByCurriculum = data
    const initialIndex = data.littleChapterConceptChips.findIndex(
      (item) => item.littleChapterId === data.recentlyLittleChapterId,
    )
    this._currentIndex = initialIndex !== -1 ? initialIndex : 0
    await this.fetchAchievements(studentId)
  }
}

export default RecentChallengeByCurriculumService
